import React, { useState, useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import OutlinedInput from '@mui/material/OutlinedInput';
import { apiNerisService } from '../../../../functions/apiNerisService';

// for items without enum
const getAutocompleteOptionsByCustomApi = (url, searchValue, extraParams) => {
  return apiNerisService
    .get(url, {
      params: {
        ...extraParams,
        search: searchValue,
      },
    })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const findMissingOptions = (options, optionsCache) => {
  const valuesSet = new Set(optionsCache.map((item) => item.value));
  return options.filter((item) => !valuesSet.has(item.value));
};

const ApiAutocompleteByEndpoint = ({
  id,
  value,
  onChange,
  endpoint,
  multiple = false,
  disabled = false,
  size = 'small',
  placeholder = 'Select option...',
  sx = {},
  extraParams,
}) => {
  const [selectedOptionsCache, setSelectedOptionsCache] = useState([]);

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOptions = useCallback(
    debounce(async (searchValue = '') => {
      if (!endpoint) {
        return;
      }

      setLoading(true);
      try {
        const response = await getAutocompleteOptionsByCustomApi(
          endpoint,
          searchValue,
          extraParams
        );

        if (response.status === 200 && response.data?.data) {
          const newOptions = Object.entries(response.data.data).map(
            ([key, value]) => ({
              value: key,
              label: value.name,
            })
          );

          setOptions(newOptions);

          setSelectedOptionsCache((prevState) => [
            ...prevState,
            ...findMissingOptions(newOptions, prevState),
          ]);
        } else throw response;
      } catch (error) {
        console.error('Error fetching options:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [endpoint]
  );

  // init selector options
  useEffect(() => {
    fetchOptions(value ?? '');
  }, []);

  const findOptionByValue = (val) => {
    return selectedOptionsCache.find((option) => option?.value === val);
  };

  return (
    <Autocomplete
      id={id}
      multiple={multiple}
      options={options}
      value={multiple ? value || [] : value || null}
      onChange={(_, newValue) => {
        onChange(newValue?.value || null);
      }}
      onInputChange={(event, newInputValue) => {
        // only users change allow
        if (!event) {
          return;
        }
        fetchOptions(newInputValue);
      }}
      loading={loading}
      disabled={disabled}
      size={size}
      sx={sx}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return findOptionByValue(option)?.label || option;
        }
        return `${option?.label ?? ''}`;
      }}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) {
          return false;
        }
        return (option?.value ?? option) === value;
      }}
      renderInput={(params) => {
        return (
          <OutlinedInput
            {...params.InputProps}
            inputProps={params.inputProps}
            placeholder={placeholder}
            size={size}
            fullWidth
            variant="outlined"
            endAdornment={
              <>
                {loading && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            }
          />
        );
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ padding: '8px 16px !important' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {option.label}
          </Typography>
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            key={option.value}
            label={option.label}
            size="small"
            {...getTagProps({ index })}
          />
        ))
      }
    />
  );
};

export default ApiAutocompleteByEndpoint;
